import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';

import { GroupIconMap, S3ObjectInput } from 'api';
import { Alert } from 'components';
import { useGroupData } from 'hooks';

import { IGroupForm } from '../../types';
import ImageUpload from './ImageUpload';
import { getGroupHexColor } from '../../helpers';
import Image from './Image';

interface IProps {
  open: boolean;
  onClose: () => void;
}

const { REACT_APP_IMGIX_HOST } = process.env;
const ICON_SIZE = 100;

const useStyles = makeStyles(theme => ({
  icon: {
    position: 'relative',
    height: ICON_SIZE,
    width: ICON_SIZE,
    backgroundSize: ICON_SIZE,
    overflow: 'hidden',
    borderRadius: 4,
    cursor: 'pointer',
  },
  toggle: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    color: theme.palette.common.white,
  },
}));

const ImagePicker: FC<IProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<IGroupForm>();
  const { groupIcons, groupColors } = useGroupData();

  const hexColor = getGroupHexColor(groupColors, values.color);

  const [deleteImageOpen, setDeleteImageOpen] = useState(false);
  const [replaceImageOpen, setReplaceImageOpen] = useState(false);
  const [replacementIcon, setReplacementIcon] = useState(null);

  function hasImage() {
    return !!values.image;
  }

  function toggleImageDelete() {
    setDeleteImageOpen(!deleteImageOpen);
  }

  function toggleImageReplace() {
    setReplaceImageOpen(!replaceImageOpen);
  }

  function changeIcon() {
    setFieldValue('icon', replacementIcon.group_icon);
    onClose();
  }

  function handleIconChange(groupIcon: GroupIconMap) {
    setReplacementIcon(groupIcon);
    hasImage ? toggleImageReplace() : changeIcon();
  }

  function handleImageSave(image: S3ObjectInput) {
    setFieldValue('image', image);
    onClose();
  }

  function handleImageDelete() {
    setFieldValue('image', null);
    setReplaceImageOpen(false);
    setDeleteImageOpen(false);
  }

  function renderDeleteImageAlert() {
    return (
      <Alert
        open={deleteImageOpen}
        onConfirm={handleImageDelete}
        onCancel={toggleImageDelete}
        message={t('groups:imageDeleteAlert')}
        confirm={t('common:delete')}
      />
    );
  }

  function renderReplaceImageAlert() {
    return (
      <Alert
        open={replaceImageOpen}
        onConfirm={() => {
          handleImageDelete();
          changeIcon();
        }}
        onCancel={toggleImageReplace}
        message={t('groups:imageReplaceAlert')}
      />
    );
  }

  function renderImage() {
    if (!hasImage()) {
      return null;
    }

    return (
      <Grid container alignItems="center" direction="column" spacing={2}>
        <Grid item>
          <Image size={250} />
        </Grid>
        <Grid item>
          <Button size="large" onClick={toggleImageDelete}>
            {t('groups:deleteImage')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderIcon(groupIcon: GroupIconMap) {
    let Icon = RadioButtonUncheckedIcon;

    if (!hasImage()) {
      Icon = values.icon === groupIcon.group_icon ? CheckCircleIcon : RadioButtonUncheckedIcon;
    }

    const src = `${REACT_APP_IMGIX_HOST}/${groupIcon.file_key}?auto=format&w=${ICON_SIZE}&h=${ICON_SIZE}`;

    return (
      <Grid item key={groupIcon.group_icon}>
        <div
          className={classes.icon}
          style={{ backgroundColor: hexColor, backgroundImage: `url(${src})` }}
          onClick={() => handleIconChange(groupIcon)}>
          <Icon className={classes.toggle} />
        </div>
      </Grid>
    );
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle disableTypography>
        <Typography variant="subtitle2">
          <Trans i18nKey="groups:image" />
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {renderDeleteImageAlert()}
        {renderReplaceImageAlert()}
        {renderImage()}
        <ImageUpload onSave={handleImageSave} hexColor={hexColor} />
        <Grid container spacing={1} justify="center">
          {groupIcons.map(renderIcon)}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ImagePicker;
